/**
 * Background Upload Manager - Handles async file uploads with progress tracking
 * 
 * This singleton service allows modals to queue file uploads and close immediately,
 * while the uploads continue in the background. Progress is tracked and can be
 * displayed via toast notifications.
 */

import { uploadEntityAttachment } from "./attachmentService";
import { compressImagesForUpload } from "../utils/imageCompression";

// Types
export interface UploadItem {
  id: string;
  file: File;
  entityType: string;
  entityId: string;
  userEmail: string;
  status: "queued" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
}

export interface UploadBatch {
  batchId: string;
  entityType: string;
  entityId: string;
  items: UploadItem[];
  onComplete?: () => void; // Called when all items in batch complete
}

type UploadListener = (batches: UploadBatch[]) => void;

class UploadManager {
  private static instance: UploadManager;
  // Upload up to this many files at once. On a dealer lot (weak LTE) the
  // bottleneck is per-request round-trip latency, not bandwidth, so a few
  // parallel uploads cut total batch time sharply without overwhelming the
  // single-worker backend (which now returns from confirm-upload immediately).
  private static readonly MAX_CONCURRENT_UPLOADS = 3;
  private batches: Map<string, UploadBatch> = new Map();
  private listeners: Set<UploadListener> = new Set();
  private activeUploads: number = 0;

  private constructor() {
    // Warn user before closing tab if uploads are in progress
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    }
  }

  static getInstance(): UploadManager {
    if (!UploadManager.instance) {
      UploadManager.instance = new UploadManager();
    }
    return UploadManager.instance;
  }

  private handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (this.hasActiveUploads()) {
      e.preventDefault();
      e.returnValue = "You have uploads in progress. Are you sure you want to leave?";
      return e.returnValue;
    }
  };

  /**
   * Check if there are any active uploads
   */
  hasActiveUploads(): boolean {
    for (const batch of this.batches.values()) {
      if (batch.items.some(item => item.status === "queued" || item.status === "uploading")) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get all active batches
   */
  getBatches(): UploadBatch[] {
    return Array.from(this.batches.values());
  }

  /**
   * Subscribe to upload state changes
   */
  subscribe(listener: UploadListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    const batches = this.getBatches();
    this.listeners.forEach(listener => listener(batches));
  }

  /**
   * Queue files for upload - returns immediately, uploads happen in background
   */
  queueUploads(
    files: File[],
    entityType: string,
    entityId: string,
    userEmail: string,
    onComplete?: () => void
  ): string {
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const items: UploadItem[] = files.map((file, index) => ({
      id: `${batchId}-${index}`,
      file,
      entityType,
      entityId,
      userEmail,
      status: "queued" as const,
      progress: 0,
    }));

    const batch: UploadBatch = {
      batchId,
      entityType,
      entityId,
      items,
      onComplete,
    };

    this.batches.set(batchId, batch);
    this.notifyListeners();

    // Top up the worker pool (no-op if already at max concurrency).
    this.pump();

    return batchId;
  }

  /**
   * Find the next queued item across all batches, or null if none remain.
   */
  private findNextQueued(): { batch: UploadBatch; item: UploadItem } | null {
    for (const batch of this.batches.values()) {
      const queuedItem = batch.items.find(item => item.status === "queued");
      if (queuedItem) {
        return { batch, item: queuedItem };
      }
    }
    return null;
  }

  /**
   * Start uploads until MAX_CONCURRENT_UPLOADS are in flight. Safe to call at
   * any time — it only ever tops the pool up, never exceeds the cap.
   */
  private pump() {
    while (this.activeUploads < UploadManager.MAX_CONCURRENT_UPLOADS) {
      const next = this.findNextQueued();
      if (!next) return;

      const { batch, item } = next;
      item.status = "uploading";
      this.activeUploads++;
      this.notifyListeners();

      // Fire and forget: processItem owns this item's lifecycle and never
      // rejects (all errors are caught and recorded on the item).
      void this.processItem(batch, item);
    }
  }

  /**
   * Run one item through compress -> upload -> status update, then free its
   * pool slot and pump the next. Each item is independent, so several run
   * concurrently.
   */
  private async processItem(batch: UploadBatch, item: UploadItem) {
    try {
      // Compress image files client-side before upload — server still generates
      // optimized display + thumbnail variants from whatever we send it.
      const [fileToUpload] = await compressImagesForUpload([item.file]).catch((err) => {
        console.error(`[UploadManager] Compression failed for ${item.file.name}, uploading original:`, err);
        return [item.file];
      });

      const result = await uploadEntityAttachment(
        fileToUpload,
        item.entityType,
        item.entityId,
        item.userEmail,
        (progress: number) => {
          item.progress = progress;
          this.notifyListeners();
        }
      );

      if (result.success) {
        item.status = "success";
        item.progress = 100;
      } else {
        item.status = "error";
        item.error = result.error || "Upload failed";
      }
    } catch (err) {
      // Defensive: uploadEntityAttachment resolves rather than throws, but a
      // bug upstream must not wedge a pool slot forever.
      item.status = "error";
      item.error = err instanceof Error ? err.message : "Upload failed";
    } finally {
      this.activeUploads--;
      this.notifyListeners();
      this.finalizeBatchIfComplete(batch);
      // A slot freed up — start the next queued item.
      this.pump();
    }
  }

  /**
   * Fire onComplete and schedule cleanup once every item in a batch is done.
   * Only the last item to finish sees all-complete, so this runs once per batch.
   */
  private finalizeBatchIfComplete(batch: UploadBatch) {
    const allComplete = batch.items.every(
      i => i.status === "success" || i.status === "error"
    );
    if (!allComplete) return;

    if (batch.onComplete) {
      batch.onComplete();
    }

    // Keep batches that have failures on screen so the user can see WHICH images
    // failed and retry them. Only auto-dismiss a fully-successful batch.
    const hasErrors = batch.items.some((i) => i.status === "error");
    if (!hasErrors) {
      setTimeout(() => {
        this.batches.delete(batch.batchId);
        this.notifyListeners();
      }, 3000);
    }
  }

  /**
   * Re-queue a single failed item and resume processing. The original File is
   * still held on the item, so this re-sends the exact image — no re-pick.
   */
  retryItem(batchId: string, itemId: string) {
    const batch = this.batches.get(batchId);
    const item = batch?.items.find((i) => i.id === itemId);
    if (item && item.status === "error") {
      item.status = "queued";
      item.progress = 0;
      item.error = undefined;
      this.notifyListeners();
      this.pump();
    }
  }

  /**
   * Re-queue every failed (not user-cancelled) item in a batch.
   */
  retryFailed(batchId: string) {
    const batch = this.batches.get(batchId);
    if (!batch) return;
    let any = false;
    for (const item of batch.items) {
      if (item.status === "error" && item.error !== "Cancelled") {
        item.status = "queued";
        item.progress = 0;
        item.error = undefined;
        any = true;
      }
    }
    if (any) {
      this.notifyListeners();
      this.pump();
    }
  }

  /**
   * Remove a batch from the tray (e.g. the user dismisses a failed batch).
   */
  dismissBatch(batchId: string) {
    this.batches.delete(batchId);
    this.notifyListeners();
  }

  /**
   * Cancel all pending uploads in a batch
   */
  cancelBatch(batchId: string) {
    const batch = this.batches.get(batchId);
    if (batch) {
      batch.items.forEach(item => {
        if (item.status === "queued") {
          item.status = "error";
          item.error = "Cancelled";
        }
      });
      this.notifyListeners();
    }
  }
}

// Export singleton instance
export const uploadManager = UploadManager.getInstance();
